import HighchartsMore from 'highcharts/highcharts-more';
import * as HighStock from 'highcharts/highstock';
import HSIndicators from 'highcharts/indicators/indicators';
import IndicatorsAll from 'highcharts/indicators/indicators-all';
import Accessibility from 'highcharts/modules/accessibility';
import AnnotationAdvance from 'highcharts/modules/annotations-advanced';
import DragPanes from 'highcharts/modules/drag-panes';
import Exporting from 'highcharts/modules/exporting';
import FullScreen from 'highcharts/modules/full-screen';
import { default as PriceIndicator, default as PriceIndicators } from 'highcharts/modules/price-indicator';
import StockTools from 'highcharts/modules/stock-tools';
import { HighChartAxisBuilder } from '../builders/highchart-axis.builder';
import { HighChartComparisonBuilder } from '../builders/highchart-comparison.builder';
import { HighChartStyleBuilder } from '../builders/highchart-style.builder';
import { HighChartBuilder } from '../builders/highchart.builder';
import { DateHelpers } from '../helpers/date-helpers';
import { DrawingToolHelpers } from '../helpers/drawing-tool-helpers';
import { IndicatorHelpers } from '../helpers/indicator-helper';
import { ObjectHelpers } from '../helpers/object-helpers';
import { OptionHelpers } from '../helpers/option-helpers';
import { StringHelpers } from '../helpers/string-helpers';
import { TraceLoggingHelpers } from '../helpers/trace-logging-helpers';
import { TIDataPointToHighChartDataPointMapper } from '../mappers/data-point/ti-chart-point-to-highchart-data-point.mapper';
import { TIDataPointToToIndiciatorPointMapper } from '../mappers/data-point/ti-chart-point-to-ti-indicator-point.mapper';
import { AuthorizationModel } from '../models/authorization/authorization.model';
import { IChartContainer } from '../models/base/chart-container.interface';
import { IChartDataService } from '../models/base/chart-data-service.interface';
import { ChartStyleSettingsModel } from '../models/chart-style/chart-style-settings.model';
import { ChartYAxisStateModel } from '../models/chart-style/chart-y-axis-state-model';
import { RealtimeTradeDataModel } from '../models/data-point/realtime-trade-data.model';
import { TIChartPointModel } from '../models/data-point/ti-chart-point.model';
import { TIComparisonDataModel } from '../models/data-point/ti-comparison-data.model';
import { TIComparisonHistoryDataModel } from '../models/data-point/ti-comparison-history-data.model';
import { TIIndicatorDataModel } from '../models/data-point/ti-indicator-data.model';
import { TradeOrderDataModel } from "../models/draw-trade-order/trade-order-data.model";
import { IndicatorOptionModel } from '../models/drop-down-list/indicator-option.model';
import { Authorization } from '../models/enum/authorization.enum';
import { IndicatorType } from '../models/enum/indicator-type.enum';
import { ScrollMode } from '../models/enum/scroll-mode.enum';
import { Timeframe } from '../models/enum/timeframe.enum';
import { CurrentPositionViewModel } from '../models/pnl/current-position-view-model';
import { IndicatorsComponent } from './chart-series/indicators.component';
import { MainChartComponent } from './chart-series/main-chart.component';
import { ComparisonSymbolLegendsComponent } from './comparison-symbol-legends.component';
import { IComponent } from './component.interface';
import { DrawEMACloudBuySellArrowComponent } from './draw-ema-cloud-buy-sell-arrow.component';
import { DrawExitOrderComponent } from './exit-orders/draw-exit-order.component';
import { DrawOrderExecutionComponent } from './draw-order-execution.component';
import { DrawTradeOrdersComponent } from './draw-trade-orders.component';
import { DrawTradePriceAnnotationComponent } from './draw-trade-price-annotation.component';
import { EMACloudSettingsPopupComponent } from './ema-cloud-settings-popup';
import { IndicatorManagerPopupComponent } from './indicator-manager-popup.component';
import { IndicatorLastPriceComponent } from './indicators-last-price.component';
import { TimeframeIconComponent } from './menu/timeframe-icon.component';
import { ToolIconComponent } from './menu/tool-icon.component';
import { PnlComponent } from './pnl.component';
import { TILogoComponent } from './ti-logo.component';
import { TooltipComponent } from './tooltip.component';

declare const window: any

HighchartsMore(HighStock);
Exporting(HighStock);
Accessibility(HighStock);
DragPanes(HighStock);
HSIndicators(HighStock);
PriceIndicators(HighStock);
IndicatorsAll(HighStock);
AnnotationAdvance(HighStock);
PriceIndicator(HighStock);
FullScreen(HighStock);
StockTools(HighStock);

HighStock.setOptions({
    lang: {
        thousandsSep: ',',
        stockTools: {
            gui: {
                curvedLine: 'Curved Line',
                customLabel: 'Label',
                curvedLineCustomAnnotation: 'Curved Line',
                emojiCustomAnnotation: 'Emoji',
                emojiCustom2Annotation: 'Emoji',
                emoji: 'Emoji',
            }
        },
        navigation: {
            popup: {
                curvedLine: 'Curved Line',
                customLabel: 'Label',
                emoji: 'Emoji',
            }
        }
    }
} as any);

export class TiChartSeriesComponent implements IComponent {
    public id = StringHelpers.newGuid();

    public highStockChart: any = null as any;

    public lastDataPoint: TIChartPointModel | null = null;
    public firstDataPoint: TIChartPointModel | null = null

    private watchExtremesChangeCallbacks = new Map<string, Array<Function>>();
    private watchRedrawChangeCallbacks = new Map<string, Array<Function>>();
    private watchChartLoadedCallbacks = new Map<string, Array<Function>>();
    private watchChartScrolledCallbacks = new Map<string, Array<Function>>();
    private watchHighChartPopupCallbacks = new Map<string, Array<Function>>();
    private watchAnnotationAddedCallbacks = new Map<string, Array<Function>>();
    private watchAnnotationAfterUpdatedCallbacks = new Map<string, Array<Function>>();
    private watchLastPriceChangeCallbacks = new Map<string, Array<Function>>();
    private watchPositionViewModelsUpdateCallbacks = new Map<string, Array<Function>>();

    public element: HTMLElement;
    public symbol = '';
    public chartAreaClassName = '.chart';
    public currentTimeframe = Timeframe.OneMinute;
    public chartElement: HTMLElement;

    public ready = false;
    public hasIndicator = false;
    public chartStarting = false;
    public preventDragging = false;
    public yAxisMoved = false;
    private requestingHistoryData = false;
    private historyDataEnded = false;
    private mouseOver = false;
    private fromMouseWheel = false;

    private extraFutureBarCount: number | null = null;
    private futureScrollingEnabled = false;
    private shouldUpdatePrimitiveChart = false;

    private readonly maxZoom = 12;
    private minZoom = -10;
    private readonly defaultZoomLevel = 0;

    public originalMinimumXValue = 0;
    public originalMaximumXValue = 0;
    public defaultTotalItemPerScreen = 0;
    public extreamMin = 0;
    public extreamMax = 0;
    public lastIndicatorXValue: number | null = null;
    public lastHiddenPointXValue = 0;
    public originalMinimumXValueWithZoom = 0;
    private originalMinMaxXValueDifferent = 0;
    private zoomPerLevel = 0;
    public currentZoom = 0;
    public lastPrice = 0;

    private currentSymbol = '';

    public base: IChartContainer;
    public highChartAxisBuilder: HighChartAxisBuilder;
    public highChartComparisonBuilder: HighChartComparisonBuilder;
    public chartService: IChartDataService;
    public highChartBuilder: HighChartBuilder;
    public highChartStyleBuilder: HighChartStyleBuilder;

    public chartStyleSettings: ChartStyleSettingsModel | null = null;
    public tiChartPoints: Array<TIChartPointModel> = [];
    public fakeTiChartPoints: Array<TIChartPointModel> = [];
    public highChartZonePoints: Array<any> = [];
    public highChartDataPoints: Array<any> = [];
    public tiIndicatorsData: Array<TIIndicatorDataModel> = [];
    public highChartIndicators: Array<IndicatorType> = [];
    public tiComparisionsData: Array<TIComparisonDataModel> = [];
    public currentPositionViewModels: Array<CurrentPositionViewModel> = [];
    public authorizations: Array<AuthorizationModel> = [];
    public hiddenAccounts: Array<string> = [];

    private currentScreen = 0;

    private mainChart: MainChartComponent;
    public indicators: IndicatorsComponent;

    private body = document.body;

    constructor(el: HTMLElement, base: IChartContainer) {
        this.element = el;
        this.base = base;
        this.chartService = base.chartService;
        this.currentScreen = this.element.clientWidth;

        this.highChartBuilder = new HighChartBuilder(this);
        this.highChartStyleBuilder = new HighChartStyleBuilder(this);
        this.highChartAxisBuilder = new HighChartAxisBuilder(this);
        this.highChartComparisonBuilder = new HighChartComparisonBuilder(this);
        this.mainChart = new MainChartComponent(this);
        this.indicators = new IndicatorsComponent(this);
        this.chartElement = this.element.findFirst(this.chartAreaClassName) as HTMLElement;

        if (this.base.marketingWebChartMode) {
            if (this.base.pnpMode) {
                this.minZoom = -24;
            } else {
                this.minZoom = -14;
            }
        } else {
            if (this.base.pnpMode) {
                this.minZoom = -24;
            }
        }

        this.subscribeHistoryScrolling();
        this.subscribeChartDataService();
        this.onBodyClick();
        this.onResize();
        this.detectMouseOver();

        if (this.base.marketingWebChartMode) {
            if (this.base.scrollMode == ScrollMode.MouseWheel) {
                this.onMouseWheelHandler();
            } else if (this.base.scrollMode == ScrollMode.ClickAndDrag) {
                this.onClickAndDragHandler();
            }
        } else {
            this.onMouseWheelHandler();

            if (!this.base.pnpMode) {
                this.onClickAndDragHandler();
            }
        }
    }

    public isPreMarketDisabled(): boolean {
        if (!this.chartStyleSettings) return true;

        return this.chartStyleSettings.isPreMarketDisabled;
    }

    public isPostMarketDisabled(): boolean {
        if (!this.chartStyleSettings) return true;

        return this.chartStyleSettings.isPostMarketDisabled;
    }

    public isPreMarketEnabled(): boolean {
        if (!this.chartStyleSettings) return false;

        return !this.chartStyleSettings?.isPreMarketDisabled;
    }

    public isPostMarketEnabled(): boolean {
        if (!this.chartStyleSettings) return false;

        return !this.chartStyleSettings?.isPostMarketDisabled;
    }

    public addHighChartIndicator(value: IndicatorType) {
        if (this.highChartIndicators.some((x: IndicatorType) => x == value)) {
            return;
        }

        this.highChartIndicators.push(value);
        TraceLoggingHelpers.log('start addHighChartIndicator');

        this.chartService.requestSaveHighChartIndicators(this.highChartIndicators);
        this.start();
    }

    public removeHighChartIndicator(value: IndicatorType) {
        const index = this.highChartIndicators.findIndex((x: IndicatorType) => x == value);
        if (index < 0) {
            return;
        }

        this.highChartIndicators.splice(index, 1);
        TraceLoggingHelpers.log('start removeHighChartIndicator');

        this.chartService.requestSaveHighChartIndicators(this.highChartIndicators);
        this.start();
    }

    public getAllActiveIndicatorOptions(): Array<IndicatorOptionModel> {
        const result: Array<IndicatorOptionModel> = [];
        const allOptions = OptionHelpers.getIndicatorOptions(this.base.tiChartSeries?.chartStyleSettings);

        for (let i = 0; i < this.tiIndicatorsData.length; i++) {
            const indicatorData = this.tiIndicatorsData[i];
            let option: IndicatorOptionModel = null;
            if (indicatorData.highChartIndicator) {
                option = allOptions.find((x: IndicatorOptionModel) => x.value == indicatorData.indicatorType);
            } else {
                const indicatorType = IndicatorHelpers.getIndicatorType(indicatorData.name);
                option = allOptions.find((x: IndicatorOptionModel) => x.value == indicatorType);
            }

            // if (option && !result.some((x: IndicatorOptionModel) => x.value == indicatorType)) {
            if (option) {
                const optionToAdd = new IndicatorOptionModel(ObjectHelpers.copy(option));
                optionToAdd.name = indicatorData.name;
                optionToAdd.chartAreaName = indicatorData.chartAreaName;
                result.push(optionToAdd);
            }
        }

        for (let i = 0; i < this.highChartIndicators.length; i++) {
            const indicatorType = this.highChartIndicators[i];
            const option = allOptions.find((x: IndicatorOptionModel) => x.value == indicatorType);
            if (option && !result.some((x: IndicatorOptionModel) => x.value == indicatorType)) {
                result.push(option);
            }
        }

        return result;
    }

    public isCurrentBarVisible(): boolean {
        if (!this.lastDataPoint) {
            return false;
        }

        const xValue = this.lastDataPoint.xValue;
        const mainChartSerie: any = this.highStockChart.series[0];
        return mainChartSerie.processedXData.some((x: number) => x == xValue);
    }

    public shouldHideHistoryModeButton(): boolean {
        if (!this.lastDataPoint) {
            return false;
        }

        const xValue = this.lastDataPoint.xValue;
        const mainChartSerie: any = this.highStockChart.series[0];
        return !mainChartSerie.processedXData?.length || mainChartSerie.processedXData.some((x: number) => x == xValue);
    }

    public isPopupVisible(): boolean {
        const popup = this.body.findFirst('.highcharts-popup:not(.custom-highchart-popup)');
        if (popup && popup.clientHeight > 0) {
            return true;
        }

        const emojiPopup = this.body.findFirst('.add-emoji-control-popup');
        if (emojiPopup && emojiPopup.classList.contains('active')) {
            return true;
        }


        return false;
    }

    public isHoverOnHighChartDrawingTools(event: any): boolean {
        const hasScrollOnElement = !!event.target && !!event.target.closest('.highcharts-stocktools-wrapper');
        if (!!hasScrollOnElement) {
            return true;
        }

        return false;
    }

    public isHoverOnCompanyDesc(event: any): boolean {
        const hasScrollOnElement = !!event.target && !!event.target.closest('.company-profile-popup');
        if (!!hasScrollOnElement) {
            return true;
        }

        return false;
    }

    public hasAuthorization(name: Authorization): boolean {
        return this.authorizations.find(x => x.name == name)?.value ?? false;
    }

    private getPointWidth(): number | null {
        const mainSeriesElement = this.element.querySelector('.main-chart-series');
        if (!mainSeriesElement) {
            return null;
        }

        const points = mainSeriesElement.querySelectorAll('.highcharts-point');
        if (!points.length) {
            return null;
        }

        const p = points[points.length - 1] as any;
        return p.getBBox().width;
    }

    public removeAllhighChartIndicator() {
        this.highChartIndicators = [];
        const allOptions = this.base.chartWrapper?.querySelectorAll<HTMLElement>('.controls .indicator .tf');
        if (allOptions)
            this.removeActiveElements(allOptions);

        const allOptionsIndList = this.base.chartWrapper?.querySelectorAll<HTMLElement>('.ind-list .tf');
        if (allOptionsIndList)
            this.removeActiveElements(allOptionsIndList);
    }

    public hasAnyDataPoints(): boolean {
        return !!this.firstDataPoint && !!this.lastDataPoint;
    }

    private removeActiveElements(options: NodeListOf<HTMLElement>) {
        options?.forEach((op: HTMLElement) => {
            op.classList.remove('active');
        });
    }

    private detectMouseOver() {
        this.base.domEvents.onMouseOver(this.id, () => {
            this.mouseOver = true;
            this.chartService.requestMouseEnter();
        });

        this.base.domEvents.onMouseLeave(this.id, () => {
            this.mouseOver = false;
            this.chartService.requestMouseLeave();
        });

        this.element.addEventListener('mousemove', (event: any) => {
            if (!this.highStockChart || !this.highStockChart.pointer) { return; }
            const pointer = this.highStockChart.pointer.normalize(event);
            const yAxis: any = this.highStockChart.yAxis[0];
            if (!yAxis) {
                return;
            }

            const toolTip = this.base.getComponentsByType(TooltipComponent)[0] as TooltipComponent;
            const comparisonSymbolLegend = this.base.getComponentsByType(ComparisonSymbolLegendsComponent)[0] as ComparisonSymbolLegendsComponent;

            const parentElementRect = this.chartElement.getBoundingClientRect();
            const yTop = pointer.y - parentElementRect.y;

            let yValue = yAxis.toValue(yTop);
            if (yValue < yAxis.min) {
                yValue = yAxis.min;
            } else if (yValue > yAxis.max) {
                yValue = yAxis.max;
            }

            toolTip?.setPrice(yValue);
            this.base.chartService.requestUpdateLastPriceClicked(yValue);

            const xAxis: any = this.highStockChart?.xAxis?.[0];
            if (xAxis) {
                const xValue = Math.round(xAxis.toValue(pointer.x));
                toolTip?.renderTooltip(xValue);
                comparisonSymbolLegend?.render(xValue);
            }
        });
    }

    private chartReadyTimer: any
    public setChartReady() {
        clearTimeout(this.chartReadyTimer);
        this.chartReadyTimer = setTimeout(() => {
            this.chartStarting = false;
            this.ready = true;
        }, 150);
    }

    private onMouseWheelHandler() {
        this.element.addEventListener('mousewheel', (event: any) => {
            if (!this.mouseOver ||
                !this.highStockChart?.pointer ||
                this.isPopupVisible() ||
                this.isHoverOnHighChartDrawingTools(event) ||
                this.isHoverOnCompanyDesc(event) ||
                this.base.isPreview ||
                !this.base.scrollingEnabled) {
                return;
            }

            const ev = this.highStockChart.pointer.normalize(event) as any;
            const doZoom = ev.ctrlKey;
            if (doZoom && this.requestingHistoryData) {
                return;
            }

            let delta = 0;

            delta = ev.detail || -(ev.wheelDelta / 120);
            delta = delta < 0 ? 1 : -1;

            ev.ctrlKey ? this.zoomHandler(delta) : this.scrollHandler(delta);
            this.stopEvent(event);
        }, { passive: false });
    }

    public setExtreamToMinMax() {
        this.setExtremes(this.extreamMin, this.extreamMax);
    }

    public setExtremes(min: number, max: number) {
        const axis = this.highStockChart?.xAxis?.[0];
        if (axis) {
            if (this.firstDataPoint && min < this.firstDataPoint.xValue - 1) {
                min = this.firstDataPoint.xValue - 1;
            }

            this.extreamMin = min;
            this.extreamMax = max;

            axis.setExtremes(min, max, true, false);
        }
    }

    private onClickAndDragHandler() {
        let pos1 = 0, pos2 = 0;
        let dragStart = false;

        if (this.base.isTouchDevice) {
            // This device has touch capabilities
            let shouldZoom = false;
            let initialDistance: number | null = null;
            let zoomimgTimer: any = null;
            const calculateDistance = (touches: TouchList) => {
                const touch1 = touches[0];
                const touch2 = touches[1];
                const dx = touch2.clientX - touch1.clientX;
                const dy = touch2.clientY - touch1.clientY;
                return Math.sqrt(dx * dx + dy * dy);
            }

            this.chartElement.addEventListener("touchstart", (e: TouchEvent) => {
                if (!this.chartStyleSettings?.mobileZoomEnabled) {
                    return;
                }

                const isAnnotation = DrawingToolHelpers.isDrawingToolAnnotation(e.target);
                if (dragStart || isAnnotation) {
                    return;
                }

                dragStart = true;

                shouldZoom = e.touches.length === 2 && (this.chartStyleSettings?.zoomEnabled ?? false);

                if (shouldZoom) {
                    initialDistance = calculateDistance(e.touches);
                }

                pos2 = e.touches[0].clientX;
            });

            this.chartElement.addEventListener('touchmove', (e: TouchEvent) => {
                if (!dragStart) {
                    return;
                }

                if (this.preventDragging) {
                    return;
                }

                pos1 = pos2 - e.touches[0].clientX;

                let differentInPOS1 = Math.abs(pos1);
                if (differentInPOS1 < 10) {
                    return;
                }

                if (shouldZoom) {
                    const currentDistance = calculateDistance(e.touches);
                    let deltaZoom = 1;

                    if (currentDistance > initialDistance) {
                        // Pinch out (zoom out)
                        deltaZoom = -1;
                    }

                    clearTimeout(zoomimgTimer);
                    zoomimgTimer = setTimeout(() => {
                        this.setExtreamToMinMax();
                        this.zoomHandler(deltaZoom);
                    }, 200);
                } else {
                    pos2 = e.touches[0].clientX;

                    let delta = pos1 / 100;
                    delta = delta < 0 ? -1 : 1;

                    this.scrollHandler(delta);
                }
            });
            this.chartElement.addEventListener('touchend', () => {
                if (!dragStart) {
                    return;
                }

                dragStart = false;
            });

            this.chartElement.addEventListener('touchcancel', () => {
                if (!dragStart) {
                    return;
                }

                dragStart = false;
            });
        } else {
            let startX, startY;
            this.chartElement.addEventListener('mousedown', (e: any) => {
                const isAnnotation = DrawingToolHelpers.isDrawingToolAnnotation(e.target);
                if (dragStart || isAnnotation) {
                    return;
                }

                dragStart = true;

                pos2 = e.clientX;
                startX = e.clientX;
                startY = e.clientY;
            });
            const moveYThreshold = 10;
            const moveXThreshold = 15;
            this.body.addEventListener('mousemove', (e: MouseEvent) => {
                if (!dragStart) {
                    return;
                }

                if (this.preventDragging) {
                    return;
                }

                if (!this.base.marketingWebChartMode && !this.chartStyleSettings?.yAxisPanningEnabled) {
                    return;
                }

                const highchartContainer = this.element.findFirst('.highcharts-container');
                if (highchartContainer) {
                    highchartContainer.style.cursor = '-webkit-grabbing';
                }

                pos1 = pos2 - e.clientX;
                pos2 = e.clientX;

                let delta = pos1 / 100;
                delta = delta < 0 ? -1 : 1;

                const shouldZoom = e.ctrlKey && (this.chartStyleSettings?.zoomEnabled ?? false);

                const currentX = e.clientX;
                const currentY = e.clientY;

                const diffX = currentX - startX;
                const diffY = currentY - startY;

                if (shouldZoom) {
                    this.zoomHandler(delta);
                } else {
                    if (Math.abs(diffX) > moveXThreshold && Math.abs(diffX) > Math.abs(diffY)) {
                        // Horizontal drag
                        this.scrollHandler(delta);

                        startX = currentX;
                        startY = currentY;
                    } else {
                        if (this.base.pnpMode) return;

                        if (Math.abs(diffY) < moveYThreshold) {
                            return
                        }

                        if (diffY > 0) {
                            this.moveYAxis(1);
                        } else {
                            this.moveYAxis(-1);
                        }

                        startX = currentX;
                        startY = currentY;
                    }
                }
            });
            this.chartElement.addEventListener('mouseleave', () => {
                if (!dragStart) {
                    return;
                }

                dragStart = false;
            });
            this.chartElement.addEventListener('mouseup', () => {
                if (!dragStart) {
                    return;
                }

                dragStart = false;
            });
        }
    }

    public getMarginMinMaxForYAxis() {
        const axis = this.highStockChart?.yAxis?.[0];
        const series = this.highStockChart?.series?.[0];

        if (!axis || !series) {
            return { min: 0, max: 0 };
        }

        // Calculate min and max based on visible data points (processed points)
        let processedMin = Number.POSITIVE_INFINITY;
        let processedMax = Number.NEGATIVE_INFINITY;

        const extremes = axis.getExtremes();

        // Iterate over the data points to find min/max of visible data
        series?.processedXData.forEach((xValue) => {
            const point = this.tiChartPoints.find(x => x.xValue == xValue);
            if (point) {
                // Find the min/max within the currently visible points
                if (point.low < processedMin) {
                    processedMin = point.low;
                }
                if (point.high > processedMax) {
                    processedMax = point.high;
                }
            }
        });

        // Ensure we have a valid min/max after processing data points
        if (processedMin === Number.POSITIVE_INFINITY || processedMax === Number.NEGATIVE_INFINITY) {
            processedMin = extremes.dataMin;
            processedMax = extremes.dataMax;
        }

        // Calculate the threshold margin (20% of the visible data range)
        const dataRange = processedMax - processedMin;
        const threshold = 0.2;
        const margin = dataRange * threshold;
        const marginMax = processedMax - margin;
        const marginMin = processedMin + margin;

        return { min: marginMin, max: marginMax, originalMin: processedMin, originalMax: processedMax };
    }

    private checkYAxisExtremes() {
        const axis = this.highStockChart?.yAxis?.[0];
        const shouldReturn = !axis || !this.yAxisMoved || !this.chartStyleSettings?.yAxisPanningEnabled || this.base.isPreview || this.base.pnpMode || (this.base.marketingWebChartMode && this.base.isExtraSmall());
        if (shouldReturn) {
            return;
        }

        // Get the current extremes of the Y-axis
        const extremes = axis.getExtremes();
        const extremeMax = extremes.max;
        const extremeMin = extremes.min;

        let newMax = extremeMax;
        let newMin = extremeMin;

        const margin = this.getMarginMinMaxForYAxis();
        const marginMax = margin.max;
        const marginMin = margin.min;

        if (marginMax < newMin) {
            TraceLoggingHelpers.log('checkYAxisExtremes: marginMax < newMin set newMin = marginMax')
            newMin = marginMax;
        }
        if (marginMin > newMax) {
            TraceLoggingHelpers.log('checkYAxisExtremes: marginMin > newMax set newMax = marginMin')
            newMax = marginMin;
        }

        // Set the new extremes with animation disabled for smoother experience
        axis.setExtremes(newMin, newMax, true, false);

        this.highChartAxisBuilder.buildAxisComponents();
    }

    private moveYAxis(delta: number) {
        const axis = this.highStockChart?.yAxis?.[0];

        if (!axis || this.base.isPreview || this.chartStyleSettings?.primitiveMode || (this.base.marketingWebChartMode && this.base.isExtraSmall())) {
            return;
        }

        this.ready = false;

        // Get the current extremes of the Y-axis
        const extremes = axis.getExtremes();
        const extremeMax = extremes.max;
        const extremeMin = extremes.min;

        // Calculate the step based on the delta and current visible range
        const step = (extremeMax - extremeMin) / 18 * delta;

        // New proposed min and max
        let newMax = extremeMax + step;
        let newMin = extremeMin + step;

        const margin = this.getMarginMinMaxForYAxis();
        const marginMax = margin.max;
        const marginMin = margin.min;

        // Clamp the new extremes to ensure at least 20% margin is visible
        if (marginMax < newMin && delta > 0) {
            this.setChartReady();
            return
        }
        if (marginMin > newMax && delta < 0) {
            this.setChartReady();
            return
        }

        this.moveYAxisMinMax(newMin, newMax);
    }

    private moveYAxisMinMax(newMin: number, newMax: number) {
        const axis = this.highStockChart?.yAxis?.[0];

        if (!axis) {
            return;
        }

        const indicatorMainYAxisId = HighChartBuilder.mainChartAreaName + '-ind-y-axis';
        const indicatorMainYAxis: any = this.highStockChart.get(indicatorMainYAxisId);

        // Set the new extremes with animation disabled for smoother experience
        this.yAxisMoved = true;
        axis.setExtremes(newMin, newMax, false, false);
        indicatorMainYAxis?.setExtremes(newMin, newMax, false, false);


        this.highChartAxisBuilder.manualMoveYAxisScale = true;
        this.highChartAxisBuilder.buildAxisComponents();
        this.setChartReady();
    }

    private expandYAxisTimer: any;
    public expandYAxis(delta: number) {
        const axis = this.highStockChart?.yAxis?.[0];
        if (!axis || this.base.isPreview || this.chartStyleSettings?.primitiveMode || (this.base.marketingWebChartMode && this.base.isExtraSmall())) {
            return;
        }

        this.ready = false;

        // Get the current extremes of the Y-axis
        const extremes = axis.getExtremes();
        const extremeMax = extremes.max;
        const extremeMin = extremes.min;

        // Determine if we're expanding or contracting
        const isExpand = delta > 0;

        // Calculate step size for expanding/contracting
        const threshold = this.base.marketingWebChartMode ? 42 : 56;
        const step = Math.abs((extremeMax - extremeMin) / threshold * delta);

        // New proposed min and max
        let newMax = extremeMax;
        let newMin = extremeMin;

        if (isExpand) {
            // When expanding, increase the range both ways
            newMax = extremeMax + step;  // Move max up
            newMin = extremeMin - step;  // Move min down
        } else {
            // When contracting, reduce the range both ways
            newMax = extremeMax - step;  // Move max down
            newMin = extremeMin + step;  // Move min up
        }

        const margin = this.getMarginMinMaxForYAxis();
        const marginMax = margin.max;
        const marginMin = margin.min;

        if (marginMax < newMin) {
            this.setChartReady();
            return
        }
        if (marginMin > newMax) {
            this.setChartReady();
            return
        }

        const indicatorMainYAxisId = HighChartBuilder.mainChartAreaName + '-ind-y-axis';
        const indicatorMainYAxis: any = this.highStockChart.get(indicatorMainYAxisId);

        this.yAxisMoved = true;
        axis.setExtremes(newMin, newMax, false, false);
        indicatorMainYAxis?.setExtremes(newMin, newMax, false, false);

        this.highChartAxisBuilder.manualExpandYAxisScale = true;
        this.highChartAxisBuilder.buildAxisComponents(true);

        clearTimeout(this.expandYAxisTimer);
        this.expandYAxisTimer = setTimeout(() => {
            this.highChartAxisBuilder.buildAxisComponents();
            this.setChartReady();
        }, 1000);
    }

    private scrollToExtraPaddingAreaTimer: any;
    private scrollToExtraPaddingAreaTimerInit = false;
    private scrollToExtraPaddingArea = false;
    public scrollHandler(delta: number) {
        const axis = this.highStockChart?.xAxis?.[0];
        if (!axis || this.base.isPreview || this.chartStyleSettings?.primitiveMode || (this.base.marketingWebChartMode && this.base.isExtraSmall())) {
            return;
        }

        this.ready = false;

        this.onScrolled();

        const rightSidePadding = this.getRightSidePadding();
        const extremes = axis.getExtremes();
        const extreamMax = extremes.max;

        const step = (extreamMax - extremes.min) / 8 * delta;
        let max = extreamMax + step;
        let min = extremes.min + step;

        if (this.isFutureBarEnable()) {
            //Scroll backward
            if (this.scrollToExtraPaddingArea && delta == -1) {
                clearTimeout(this.scrollToExtraPaddingAreaTimer);
                this.scrollToExtraPaddingAreaTimer = setTimeout(() => {
                    this.scrollToExtraPaddingArea = false;
                }, 50);

                let minXtream = this.originalMinimumXValueWithZoom;
                if (this.originalMinimumXValueWithZoom >= this.originalMaximumXValue) {
                    minXtream -= this.chartStyleSettings?.futureScrollingMinDisplayBarCount ?? 0;
                }


                this.setExtremes(minXtream, this.originalMaximumXValue);
                this.requestSaveFutureBarCount(0);
                return;
            }

            //Scroll forward
            if (max > this.originalMaximumXValue) {
                if (!this.scrollToExtraPaddingArea) {
                    if (this.scrollToExtraPaddingAreaTimerInit) {
                        clearTimeout(this.scrollToExtraPaddingAreaTimer);
                        this.scrollToExtraPaddingAreaTimer = setTimeout(() => {
                            this.scrollToExtraPaddingArea = true;
                        }, this.base.marketingWebChartMode ? 50 : 30);
                    } else {
                        this.scrollToExtraPaddingAreaTimerInit = true;
                        this.scrollToExtraPaddingArea = true;
                    }

                    let minXtream = this.originalMinimumXValueWithZoom;
                    if (this.originalMinimumXValueWithZoom >= this.originalMaximumXValue) {
                        minXtream -= this.chartStyleSettings?.futureScrollingMinDisplayBarCount ?? 0;
                    }

                    this.setExtremes(minXtream, this.originalMaximumXValue);
                    this.requestSaveFutureBarCount(0);
                    return;
                } else {
                    const series: any = this.highStockChart.series?.[0];
                    if (!series) {
                        return;
                    }
                    const visibleDataPoints = series?.processedXData.length;
                    let shouldStop = false;
                    const minBarCount = this.chartStyleSettings?.futureScrollingMinDisplayBarCount;
                    if (delta == 1) {
                        if (visibleDataPoints <= minBarCount) {
                            shouldStop = true;
                        } else {
                            const targetPoint = series.points[series.points.length - (minBarCount + 3)];
                            if (targetPoint && targetPoint.graphic?.element) {
                                const svgRect = targetPoint.graphic.element.getBoundingClientRect();
                                const isVisible = (
                                    svgRect.top < window.innerHeight &&
                                    svgRect.bottom > 0 &&
                                    svgRect.left < window.innerWidth &&
                                    svgRect.right > 0
                                );

                                if (!isVisible) {
                                    shouldStop = true;
                                }
                            }
                        }
                    }

                    if (shouldStop) {
                        return;
                    }
                }

                if (max > this.originalMaximumXValue + rightSidePadding) {
                    this.setExtreamToMaxRightSidePadding(true);
                    return;
                }

                const extraBarCount = max - this.originalMaximumXValue;

                this.requestSaveFutureBarCount(extraBarCount);
            } else {
                //Scroll backward
                clearTimeout(this.scrollToExtraPaddingAreaTimer);
                this.scrollToExtraPaddingArea = false;

                this.requestSaveFutureBarCount(0);
            }
        } else {
            if (max > this.originalMaximumXValue) {
                this.setExtremes(this.originalMinimumXValueWithZoom, this.originalMaximumXValue);
                return;
            }
        }

        const series: any = this.highStockChart.series[0];
        const firstVisibleBar = series?.processedXData[0];
        const oldestData = series?.xData[0];
        const historyScrollingEnded = delta == -1 && oldestData != null && (this.requestingHistoryData || firstVisibleBar < oldestData || (this.historyDataEnded && min < oldestData));
        if (historyScrollingEnded) {
            TraceLoggingHelpers.log('End of history', min);
            if (this.firstDataPoint && min < this.firstDataPoint.xValue - 1) {
                this.extreamMin = this.firstDataPoint.xValue - 1;
            }

            if (this.lastDataPoint && max > this.lastDataPoint.xValue) {
                this.extreamMax = this.lastDataPoint.xValue;
            }

            this.setExtremes(this.extreamMin, this.extreamMax);
            return;
        }

        this.extreamMin = min;
        this.extreamMax = max;

        if (delta == -1) {
            this.fromMouseWheel = true;
        }

        this.setExtremes(this.extreamMin, this.extreamMax);
        this.checkYAxisExtremes();
        this.setChartReady();
    }

    public setExtreamToMaxRightSidePadding(persisting?: boolean) {
        const rightSidePadding = this.getRightSidePadding();

        this.setExtremes(this.originalMinimumXValueWithZoom, this.originalMaximumXValue + rightSidePadding);
        this.extraFutureBarCount = rightSidePadding;
        this.scrollToExtraPaddingArea = true;
        this.scrollToExtraPaddingAreaTimerInit = true;

        if (persisting)
            this.chartService.requestSaveExtraFutureBarCount(rightSidePadding);
    }

    private drawExitOrderComponent: DrawExitOrderComponent | null = null;
    private getDrawExitOrderComponent(): DrawExitOrderComponent | null {
        if (this.drawExitOrderComponent) {
            return this.drawExitOrderComponent;
        }

        const drawExitOrderComp = this.base.getComponentsByType(DrawExitOrderComponent)?.[0] as DrawExitOrderComponent;
        this.drawExitOrderComponent = drawExitOrderComp ?? null;
        return this.drawExitOrderComponent;
    }

    private drawTradeOrdersComponent: DrawTradeOrdersComponent | null = null;
    private getDrawTradeOrdersComponent(): DrawTradeOrdersComponent | null {
        if (this.drawTradeOrdersComponent) {
            return this.drawTradeOrdersComponent;
        }

        const drawTradeOrdersComp = this.base.getComponentsByType(DrawTradeOrdersComponent)?.[0] as DrawTradeOrdersComponent;
        this.drawTradeOrdersComponent = drawTradeOrdersComp ?? null;
        return this.drawTradeOrdersComponent;
    }

    public setExtreamToExitOrderIfExist() {
        const pnlComp = this.base.getComponentsByType(PnlComponent)?.[0] as PnlComponent;
        const drawExitOrderComp = this.getDrawExitOrderComponent();
        const drawTradeOrdersComp = this.getDrawTradeOrdersComponent();
        let extraFutureBarCount = this.chartStyleSettings?.rightEdgePaddingSaveEnabled ? (this.chartStyleSettings.extraFutureBarCount ? this.chartStyleSettings.extraFutureBarCount : this.chartStyleSettings.rightEdgePaddingBarsDefault) : 0;

        if (extraFutureBarCount < 0) {
            extraFutureBarCount = 0;
        }
        
        if (pnlComp?.selectedAccount && drawExitOrderComp?.exitOrderPlotLine) {
            if (drawTradeOrdersComp?.hasAnyTradeOrders() || !drawExitOrderComp?.exitOrderPlotLine?.hide) {
                this.setExtreamToExitOrderPosition();
            } else {
                this.setExtremes(this.originalMinimumXValueWithZoom, this.originalMaximumXValue + extraFutureBarCount);
            }
        } else {
            if (drawTradeOrdersComp?.hasAnyTradeOrders()) {
                this.setExtreamToExitOrderPosition();
            } else {
                this.setExtremes(this.originalMinimumXValueWithZoom, this.originalMaximumXValue + extraFutureBarCount);
            }
        }
    }

    private setExtreamToExitOrderPosition() {
        const extraPaddingNeeded = this.getExtraPaddingBarCountForDisplay() ?? 0;
        if (!extraPaddingNeeded || !this.chartStyleSettings?.chartSettingsPopupModel?.enableRightEdgePadding) {
            return;
        }

        const minimumPaddingRequired = this.originalMaximumXValue + extraPaddingNeeded;
        this.setExtremes(this.originalMinimumXValueWithZoom + extraPaddingNeeded, minimumPaddingRequired);
        this.scrollToExtraPaddingArea = true;
        this.scrollToExtraPaddingAreaTimerInit = true;
    }

    private getExtraPaddingBarCountForDisplay(): number {
        const drawTradeOrdersComp = this.getDrawTradeOrdersComponent();
        const exitOrder = this.getDrawExitOrderComponent();
        let exitOrderWidth = exitOrder?.elementMaximumWidth ?? 0;

        if (drawTradeOrdersComp) {
            const tradeOrdersWidth = drawTradeOrdersComp.elementMaximumWidth;
            if (tradeOrdersWidth > exitOrderWidth) {
                exitOrderWidth = tradeOrdersWidth;
            }
        }

        let extraPaddingLeft = 20;

        // More accurate calculation using axis extremes and chart width
        const xAxis = this.highStockChart?.xAxis?.[0];
        if (!xAxis) {
            return 0;
        }

        const extremes = xAxis.getExtremes();
        const visibleBars = extremes.max - extremes.min;

        // Get the chart's plot width (the actual drawable area)
        const plotWidth = this.highStockChart?.plotWidth ?? this.element.clientWidth;

        // Calculate pixels per bar more accurately
        let pixelsPerBar = plotWidth / visibleBars;

        // Fallback: if calculation seems off, use minimum value
        if (pixelsPerBar <= 0 || !isFinite(pixelsPerBar)) {
            pixelsPerBar = 1;
        }

        if (this.currentZoom <= -9) {
            extraPaddingLeft += 30;
        }

        let extraPaddingNeeded = (exitOrderWidth + extraPaddingLeft) / pixelsPerBar;

        // Round up to ensure we have enough padding
        return Math.ceil(extraPaddingNeeded);
    }

    public zoomHandler(delta: number, skipCheck?: boolean) {
        if (!skipCheck && ((this.base.isTouchDevice ? !(this.chartStyleSettings?.mobileZoomEnabled ?? false) : !(this.chartStyleSettings?.zoomEnabled ?? false)) || this.base.isPreview || this.chartStyleSettings?.primitiveMode || (this.base.marketingWebChartMode && this.base.isExtraSmall()))) {
            return;
        }

        let zoom = this.currentZoom;
        if (delta > 0) {
            if (zoom >= this.maxZoom) {
                TraceLoggingHelpers.log('zoom reach max');
                return;
            }

            zoom++;
        } else if (delta < 0) {
            if (zoom <= this.minZoom) {
                TraceLoggingHelpers.log('zoom reach min');
                return;
            }

            zoom--;
        } else {
            return;
        }

        this.setZoom(zoom);
    }

    public setZoom(zoom: number) {
        const axis = this.highStockChart.xAxis?.[0];
        if (!axis) {
            return;
        }

        const series = this.highStockChart.series?.[0];
        if (!series) {
            return;
        }

        this.currentZoom = zoom;
        this.ready = false;
        const extremes = axis.getExtremes();

        this.calculateZoomPerLevel();

        this.extreamMax = extremes.max;
        this.calcExtremMinFromZoom();

        TraceLoggingHelpers.log('current zoom : ' + this.currentZoom + 'extreamMin : ' + this.extreamMin + 'extreamMax : ' + this.extreamMax)
        this.fromMouseWheel = true;

        if (this.isFutureBarEnable()) {
            let shouldReset = this.extreamMin >= this.lastDataPoint.xValue;
            const targetPoint = series.points.find((x: any) => x?.x == this.lastDataPoint.xValue);
            if (!shouldReset && targetPoint && targetPoint.graphic?.element) {
                const svgRect = targetPoint.graphic.element.getBoundingClientRect();
                const isVisible = (
                    svgRect.top < window.innerHeight &&
                    svgRect.bottom > 0 &&
                    svgRect.left < window.innerWidth &&
                    svgRect.right > 0
                );

                if (!isVisible) {
                    shouldReset = true;
                }
            }

            if (shouldReset) {
                this.extreamMin = this.lastDataPoint.xValue - (this.chartStyleSettings?.futureScrollingMinDisplayBarCount ?? 0);
                this.extreamMax = this.originalMaximumXValue;
            }
        }

        this.setChartReady();

        const chartPadding = this.getRightSidePadding();
        if (this.extreamMax > this.originalMaximumXValue + chartPadding) {
            this.extreamMax = this.originalMaximumXValue + chartPadding;
        }
        
        this.setExtremes(this.extreamMin, this.extreamMax);

        this.chartStyleSettings.zoomLevel = this.currentZoom;
        this.base.chartService.requestSetZoomLevel(this.currentZoom);
        this.showHideTimespan();
    }

    private showHideTimespan() {
        if (!this.base.pnpMode) {
            return;
        }

        const control = this.base.getComponentsByType(TimeframeIconComponent)[0] as TimeframeIconComponent;
        if (!control) {
            return;
        }

        const zoom = this.getDefaultZoomLevel();
        if (zoom != null && zoom == this.chartStyleSettings?.zoomLevel) {
            control.showTimespan();
        } else {
            control.hideTimespan();
        }
    }

    private isFutureBarEnable(): boolean {
        return (!this.base.pnpMode && this.chartStyleSettings && this.chartStyleSettings.supportsFutureScrolling && this.futureScrollingEnabled && this.chartStyleSettings.futureScrollingBarCount > 0);
    }

    private historyLoadTimmer: any = null;
    private subscribeHistoryScrolling() {
        this.subscribeExtremeChange(this.id, () => {
            setTimeout(() => this.setChartComponentStyles(), 150);

            const series: any = this.highStockChart.series[0];
            const firstVisibleBar = series?.processedXData[0];
            const oldestData = series?.xData[0];
            this.setChartReady();
            if (this.historyDataEnded || this.base.isPreview) {
                return;
            }

            if (firstVisibleBar - 10 < oldestData && !this.requestingHistoryData && this.fromMouseWheel) {
                this.requestingHistoryData = true;
                clearTimeout(this.historyLoadTimmer);
                this.historyLoadTimmer = setTimeout(() => {
                    TraceLoggingHelpers.log('this.requestingHistoryData = true');
                    this.ready = false;
                    this.fromMouseWheel = false;
                    this.chartService.requestHistory();
                }, 100);
            }
        });
    }

    private calculateZoomPerLevel() {
        if (this.base.marketingWebChartMode) {
            const ZOOMING_IN_RANGE_ONE = -3;
            const ZOOMING_IN_RANGE_TWO = this.base.pnpMode ? -12 : -6;
            if (this.currentZoom > this.defaultZoomLevel) {
                // Zooming in, past default chart size.
                this.zoomPerLevel = this.originalMinMaxXValueDifferent / 12;
            } else if (this.currentZoom > ZOOMING_IN_RANGE_ONE) {
                this.zoomPerLevel = this.originalMinMaxXValueDifferent / 6;
            } else if (this.currentZoom > ZOOMING_IN_RANGE_TWO) {
                this.zoomPerLevel = this.originalMinMaxXValueDifferent / 2.5;
            } else {
                if (this.base.pnpMode) {
                    this.zoomPerLevel = this.originalMinMaxXValueDifferent * 1.6;
                } else {
                    const ZOOMING_IN_RANGE_THREE = -8;
                    const ZOOMING_IN_RANGE_FOUR = -9;
                    const ZOOMING_IN_RANGE_FIVE = -10;
                    if (this.currentZoom > ZOOMING_IN_RANGE_THREE) {
                        this.zoomPerLevel = this.originalMinMaxXValueDifferent / 2.5;
                    } else if (this.currentZoom > ZOOMING_IN_RANGE_FOUR) {
                        this.zoomPerLevel = this.originalMinMaxXValueDifferent / 2;
                    } else if (this.currentZoom > ZOOMING_IN_RANGE_FIVE) {
                        this.zoomPerLevel = this.originalMinMaxXValueDifferent * 1.3;
                    } else {
                        this.zoomPerLevel = this.originalMinMaxXValueDifferent * 1.6;
                    }
                }
            }
        } else {
            const ZOOMING_IN_RANGE_ONE = -3;
            const ZOOMING_IN_RANGE_TWO = -6;
            if (this.currentZoom > this.defaultZoomLevel) {
                // Zooming in, past default chart size.
                this.zoomPerLevel = this.originalMinMaxXValueDifferent / 12;
            } else if (this.currentZoom > ZOOMING_IN_RANGE_ONE) {
                this.zoomPerLevel = this.originalMinMaxXValueDifferent / 6;
            } else if (this.currentZoom > ZOOMING_IN_RANGE_TWO) {
                this.zoomPerLevel = this.originalMinMaxXValueDifferent / 3;
            } else {
                const ZOOMING_IN_RANGE_THREE = -8;
                const ZOOMING_IN_RANGE_FOUR = -9;
                const ZOOMING_IN_RANGE_FIVE = -10;
                if (this.currentZoom > ZOOMING_IN_RANGE_THREE) {
                    this.zoomPerLevel = this.originalMinMaxXValueDifferent / 2.5;
                } else if (this.currentZoom > ZOOMING_IN_RANGE_FOUR) {
                    this.zoomPerLevel = this.originalMinMaxXValueDifferent / 2;
                } else if (this.currentZoom > ZOOMING_IN_RANGE_FIVE) {
                    this.zoomPerLevel = this.originalMinMaxXValueDifferent * 1.3;
                } else {
                    this.zoomPerLevel = this.originalMinMaxXValueDifferent * 1.6;
                }
            }
        }
    }

    private setRangeTimeframeExtreamMin() {
        if (this.currentTimeframe == Timeframe.YearToDateRange) {
            this.setYTDExtreamMin();
        } else if (this.currentTimeframe == Timeframe.OneMonthRange) {
            this.setExtreamMinFromMonthBehide(-1);
        } else if (this.currentTimeframe == Timeframe.SixMonthRange) {
            this.setExtreamMinFromMonthBehide(-6);
        } else if (this.currentTimeframe == Timeframe.OneYearRange) {
            this.setExtreamMinFromMonthBehide(-12);
        } else if (this.currentTimeframe == Timeframe.FiveYearRange) {
            this.setExtreamMinFromMonthBehide(-60);
        }
    }

    private setExtreamMinFromMonthBehide(month: number) {
        const currentDate = new Date();
        const startDate = DateHelpers.addMonths(currentDate, month);
        let x = -1;
        for (let i = this.tiChartPoints.length - 1; i >= 0; i--) {
            const point = this.tiChartPoints[i];

            x = point.xValue;

            if (startDate.valueOf() > point.start.valueOf()) {
                break;
            }
        }

        this.extreamMin = x;
    }

    private setYTDExtreamMin() {
        const currentDate = new Date();
        const year = currentDate.getFullYear();
        let x = -1;
        for (let i = this.tiChartPoints.length - 1; i >= 0; i--) {
            const point = this.tiChartPoints[i];

            if (point.start.getFullYear() === year && point.start.valueOf() <= currentDate.valueOf()) {
                x = point.xValue;
            } else {
                break;
            }
        }

        this.extreamMin = x;
    }

    public calcExtremMinFromZoom() {
        if (this.base.isPreview) {
            return;
        }

        // TraceLoggingHelpers.log(this.extreamMax, this.defaultTotalItemPerScreen, this.zoomPerLevel, this.currentZoom)
        this.extreamMin = (this.extreamMax - this.defaultTotalItemPerScreen) + (this.zoomPerLevel * this.currentZoom);
        this.originalMinimumXValueWithZoom = (this.originalMaximumXValue - this.defaultTotalItemPerScreen) + (this.zoomPerLevel * this.currentZoom);
    }

    public getRightSidePadding(): number {
        const drawTradeOrdersComp = this.getDrawTradeOrdersComponent();
        const exitOrder = this.getDrawExitOrderComponent();
        const extraPaddingNeeded = this.getExtraPaddingBarCountForDisplay() ?? 0;
        if (extraPaddingNeeded != null && (drawTradeOrdersComp?.elementMaximumWidth > 0 || exitOrder?.elementMaximumWidth > 0)) {
            return extraPaddingNeeded;
        }

        const basePadding = this.chartStyleSettings?.futureScrollingBarCount ?? 0;
        return basePadding;
    }

    public calcMinMaxTimeframeFromXValue(xValue: number, updateOriginalDiff?: boolean) {
        if (this.base.isPreview || this.chartStyleSettings?.primitiveMode) {
            if (this.tiChartPoints?.length) {
                this.originalMinimumXValue = this.tiChartPoints[0].xValue;
                this.originalMaximumXValue = this.tiChartPoints[this.tiChartPoints.length - 1].xValue;
            }

            return;
        }

        this.defaultTotalItemPerScreen = this.getDefaultTotalXValuePerScreen();
        this.originalMaximumXValue = xValue;

        this.originalMinimumXValue = xValue - this.defaultTotalItemPerScreen;

        this.originalMinimumXValueWithZoom = (this.originalMaximumXValue - this.defaultTotalItemPerScreen) + (this.zoomPerLevel * this.currentZoom);
        if (this.originalMinimumXValueWithZoom > this.originalMaximumXValue) {
            this.originalMinimumXValueWithZoom = this.originalMinimumXValue;
        }

        if (updateOriginalDiff) {
            this.originalMinMaxXValueDifferent = this.originalMaximumXValue - this.originalMinimumXValue;
        }
    }

    private stopEvent(event: any) {
        if (!event) {
            return;
        }

        if (event.preventDefault) {
            event.preventDefault();
        }

        if (event.stopPropagation) {
            event.stopPropagation();
        }

        event.cancelBubble = true;
    }

    private subscribeChartDataService() {
        this.subscribeChartStyleData();
        this.subscribeChartDataInit();
        this.subscribeChartComparisionsDataInit();
        this.subscribeChartIndicatorsDataInit();
        this.subscribeRealtimeData();
        this.subscribe10sRefreshChartData();
        this.subscribe10sRefreshIndicatorsData();
        this.subscribeUpdateChartHistoryData();
        this.subscribeUpdateChartComparisonHistoryData();
        this.subscribeUpdateHistoryIndicatorsData();
        this.subscribeWindowMinimizeUpdate();
        this.subscribeChartDataLastPriceChange();
        this.subscribeChartDataPositionViewModel();
        this.subscribeAuthorizationsData();
        this.subscribeTradeOrdersData();
    }

    public setPanel() {
        const tilogo = this.base.getComponentsByType(TILogoComponent)[0] as TILogoComponent;
        tilogo?.show(this.currentTimeframe);

        const timeframe = this.base.getComponentsByType(TimeframeIconComponent)[0] as TimeframeIconComponent;
        timeframe?.setCurrentTimeframe(this.currentTimeframe);
    }

    public hasAnyComparisonSymbols(): boolean {
        return this.tiComparisionsData?.length > 0;
    }

    private updateChartStyleDataTimer: any;
    private subscribeChartStyleData() {
        this.chartService.subscribeChartStyleData((chartStyleSettings: ChartStyleSettingsModel) => {
            clearTimeout(this.updateChartStyleDataTimer);
            this.updateChartStyleDataTimer = setTimeout(() => {

                window.thousandSeparator = chartStyleSettings.thousandSeparator;
                window.decimalSeparator = chartStyleSettings.decimalSeparator;

                if (this.chartStyleSettings?.rightEdgePaddingSaveEnabled) {
                    let barCount = chartStyleSettings.rightEdgePaddingBarsDefault;
                     if (!chartStyleSettings.extraFutureBarCount && this.extraFutureBarCount != null) {
                        barCount = this.extraFutureBarCount;
                    } else if (chartStyleSettings.extraFutureBarCount) {
                        barCount = chartStyleSettings.extraFutureBarCount;
                    }

                    if (barCount < 0) {
                        barCount = 0;
                    }
                    
                    this.extraFutureBarCount = barCount;
                }
                
                // this.extraFutureBarCount = null;
                // chartStyleSettings.rightEdgePaddingSaveEnabled ? (chartStyleSettings.extraFutureBarCount ? chartStyleSettings.extraFutureBarCount : chartStyleSettings.rightEdgePaddingBarsDefault) : 0;
                
                this.futureScrollingEnabled = chartStyleSettings.futureScrollingEnabled;

                this.highChartStyleBuilder.createCrosshairsStyle(chartStyleSettings);
                this.highChartStyleBuilder.createGridLinesStyle(chartStyleSettings);
                this.highChartStyleBuilder.createPlotbandsStyle(chartStyleSettings);

                if (this.base.pnpMode && this.chartStyleSettings) {
                    chartStyleSettings.selectedLineColor = this.chartStyleSettings.selectedLineColor;
                }

                if ((this.chartStyleSettings && this.chartStyleSettings.equals(chartStyleSettings))) {
                    this.highChartAxisBuilder.buildAxisComponents();
                    return;
                }

                this.chartStyleSettings = chartStyleSettings;
                if (this.chartStyleSettings?.isDarkTheme) {
                    this.element.classList.add('dark-theme');
                    this.base.chartWrapper?.classList.add('dark-theme');
                } else {
                    this.element.classList.remove('dark-theme');
                    this.base.chartWrapper?.classList.remove('dark-theme');
                }

                this.restoreHighChartIndicators();
                if (this.tiChartPoints?.length) {
                    this.start();
                    setTimeout(() => {
                        this.highChartComparisonBuilder.buildComparisonsComponents(0, true);
                    }, 150);
                }
            }, 100);
        });
    }

    private restoreHighChartIndicators() {
        this.removeAllhighChartIndicator();
        const items = this.chartStyleSettings?.highChartIndicators?.split(',') ?? [];
        for (const item of items) {
            const value = parseInt(item, 10);
            const op = this.base.chartWrapper?.querySelector('.indicator .tf[data-value="' + value + '"]');
            const opIndList = this.base.chartWrapper?.querySelector('.ind-list .tf[data-value="' + value + '"]');
            if (op) {
                op.classList.add('active');

                if (value > 210)
                    this.highChartIndicators.push(value);
            } else if (opIndList) {
                opIndList.classList.add('active');

                if (value > 210)
                    this.highChartIndicators.push(value);
            }
        }
    }

    private setActiveIndicators() {
        const allActiveIndicators = this.getAllActiveIndicatorOptions();
        this.base.chartWrapper?.querySelectorAll<HTMLElement>('.indicator li').forEach(x => {
            const value = parseInt(x.dataset.value, 10);
            const isExist = allActiveIndicators.some(ind => ind.value == value);
            if (isExist) {
                x.classList.add('active');
            } else {
                x.classList.remove('active');
            }
        });
        this.base.chartWrapper?.querySelectorAll<HTMLElement>('.ind-list li').forEach(x => {
            const value = parseInt(x.dataset.value, 10);
            const isExist = allActiveIndicators.some(ind => ind.value == value);
            if (isExist) {
                x.classList.add('active');
            } else {
                x.classList.remove('active');
            }
        });
    }

    private subscribeChartDataInit() {
        this.chartService.subscribeInitChartData((data: Array<TIChartPointModel>, timeframe: Timeframe, symbol: string) => {
            this.historyDataEnded = false;
            this.requestingHistoryData = false;
            this.shouldUpdatePrimitiveChart = false;

            if (this.chartStyleSettings?.primitiveMode && this.highStockChart && this.currentSymbol && this.currentSymbol == symbol) {
                this.shouldUpdatePrimitiveChart = true;
            }

            this.currentSymbol = symbol;
            this.symbol = symbol;
            this.currentTimeframe = timeframe;
            this.tiChartPoints = data;
        });
    }

    private subscribeChartComparisionsDataInit() {
        this.chartService.subscribeInitChartComparisonsData((data: Array<TIComparisonDataModel>) => {
            this.tiComparisionsData = data;
            this.highChartComparisonBuilder.buildComparisonsComponents(0, true);
        });
    }

    private subscribeChartIndicatorsDataInit() {
        this.chartService.subscribeInitChartIndicatorsData((data: Array<TIIndicatorDataModel>) => {
            this.addIndicatorsData(data);
            this.start();
        });
    }

    private addIndicatorsData(initiailizeData: Array<TIIndicatorDataModel>) {
        if (!initiailizeData?.length) {
            this.tiIndicatorsData = [];
            this.hasIndicator = false;
            return;
        }

        this.hasIndicator = true;
        this.tiIndicatorsData = TIDataPointToToIndiciatorPointMapper.map(initiailizeData, this.tiChartPoints);
    }

    public hideWithOpacity() {
        this.element.querySelectorAll<HTMLElement>('.highcharts-series-group').forEach((el: HTMLElement) => {
            el.style.visibility = 'hidden';
        });
        this.element.querySelectorAll<HTMLElement>('.highcharts-plot-band').forEach((el: HTMLElement) => {
            el.style.visibility = 'hidden';
        });
    }

    public showWithOpacity() {
        this.element.querySelectorAll<HTMLElement>('.highcharts-series-group').forEach((el: HTMLElement) => {
            el.style.visibility = '';
        });
        this.element.querySelectorAll<HTMLElement>('.highcharts-plot-band').forEach((el: HTMLElement) => {
            el.style.visibility = '';
        });
    }

    private startTimer: any;
    private waitForRetryStart = false;
    private afterStartTimer: any;
    public start() {
        const internalStart = () => {
            if (!this.chartStyleSettings) {
                return;
            }

            this.chartStarting = true;
            this.waitForRetryStart = false;
            this.scrollToExtraPaddingAreaTimerInit = false;
            this.scrollToExtraPaddingArea = false;
            this.indicators.canUpdateEMACloud = false;

            if (this.highStockChart?.destroy) {
                this.highStockChart.destroy();
                this.highStockChart = null;
            }

            this.ready = false;
            this.base.hideTooltip();
            const highChartConfig = this.highChartBuilder.getConfig();
            this.lastDataPoint = this.tiChartPoints[this.tiChartPoints.length - 1];
            if (!this.lastDataPoint) {
                return;
            }

            this.calcMinMaxTimeframeFromXValue(this.lastDataPoint.xValue, true);
            TraceLoggingHelpers.log('create HighStock.stockChart  is pnp: ' + this.base.pnpMode, { chart: highChartConfig, chartStyle: this.chartStyleSettings });

            this.setZoomLevel();

            this.base.setCurrentFirstAndLastPointChangesFromCloseColor(this.lastDataPoint.close, this.originalMinimumXValueWithZoom, this.originalMaximumXValue);

            this.highChartStyleBuilder.createMainChartStyle(this.chartStyleSettings);
            this.highChartStyleBuilder.createIndicatorStyle(this.tiIndicatorsData);

            this.highStockChart = HighStock.stockChart(this.chartElement, highChartConfig);

            const series: any = highChartConfig?.series;
            this.highChartZonePoints = series && series[0] ? (series[0].zones ?? []) : [];
            this.highChartDataPoints = series && series[0] ? (series[0].data ?? []) : [];

            this.extreamMin = this.originalMinimumXValueWithZoom;
            this.extreamMax = this.originalMaximumXValue;

            this.firstDataPoint = this.tiChartPoints[0];
            TraceLoggingHelpers.log('create this.chart ', this.highStockChart);

            if (this.tiIndicatorsData?.length) {
                this.lastIndicatorXValue = this.tiIndicatorsData[0].points?.length ? this.tiIndicatorsData[0].points[this.tiIndicatorsData[0].points.length - 1].xValue : null;
            }

            const indicatorLastPriceComponent = this.base.getComponentsByType(IndicatorLastPriceComponent)[0] as IndicatorLastPriceComponent;
            indicatorLastPriceComponent?.createLastPriceForIndicators();

            const drawOrderExecutionComponent = this.base.getComponentsByType(DrawOrderExecutionComponent)[0] as DrawOrderExecutionComponent;
            drawOrderExecutionComponent?.createElement();

            const drawTradePriceAnnotationComponent = this.base.getComponentsByType(DrawTradePriceAnnotationComponent)[0] as DrawTradePriceAnnotationComponent;
            drawTradePriceAnnotationComponent?.createElement();

            if (this.currentZoom != 0) {
                this.calculateZoomPerLevel();
                this.calcExtremMinFromZoom();
            }

            if (this.currentTimeframe >= Timeframe.OneMonthRange) {
                this.setRangeTimeframeExtreamMin();
            }

            this.showWithOpacity();

            if (this.base.pnpMode) {
                this.setExtreamToMinMax();
            } else {
                if (this.chartStyleSettings.primitiveMode) {
                    this.zoomPrimitiveChartToDate();
                    return;
                }

                this.setExtreamToMinMax();
            }

            this.setChartReady();
            this.setChartComponentStyles();
            this.setPanel();

            clearTimeout(this.afterStartTimer);
            this.afterStartTimer = setTimeout(() => {

                const toolIconComponent = this.base.getComponentsByType(ToolIconComponent)[0] as ToolIconComponent;
                toolIconComponent?.addToolIconComponents(this.chartStyleSettings?.startDrawingToolsExpanded ?? false);

                this.setActiveIndicators();
                this.highChartAxisBuilder.buildAxisComponents();

                if (this.lastDataPoint) {
                    this.base.setCurrentFirstAndLastPointChangesFromCloseColor(this.lastDataPoint.close);
                }

                this.base.drawingToolsManager?.sync(0);

                this.setEMACloundData();
            }, 500);
        }

        if (this.chartStyleSettings?.advancedOEPPopoutMode) {
            return;
        }
        
        if (!this.base.marketingWebChartMode) {
            if (!this.base.isBodySizeValid()) {
                // This is a workaround for the issue where the chart is not initialized properly when the body size is 1x1.
                // It happens when the chart is restored with a minimized state.
                this.waitForRetryStart = true;
                return;
            }
        }

        clearTimeout(this.startTimer);
        this.startTimer = setTimeout(() => {
            if (this.shouldUpdatePrimitiveChart) {
                this.updatePrimitiveChartDataPoints();
            } else {
                internalStart();
            }
        }, 50);
    }

    private extreamSet = false;
    private positionsSet = false;
    private ordersSet = false;
    private chartLoaded = false;
    private setExtreamAfterChartStartTimer: any;
    private setExtreamAfterChartStart() {
        const yAxis = this.highStockChart?.yAxis?.[0];
        const drawTradeOrdersComp = this.getDrawTradeOrdersComponent();
        const drawExitOrderComp = this.getDrawExitOrderComponent();

        if (drawExitOrderComp == null || !drawExitOrderComp.initialize || drawTradeOrdersComp == null || !drawTradeOrdersComp.initialize) {
            clearTimeout(this.setExtreamAfterChartStartTimer);
            this.setExtreamAfterChartStartTimer = setTimeout(() => {
                this.setExtreamAfterChartStart();
            }, 350);
            return;
        }

        const shouldSetRightEdgePaddingForEntryOrLimitOrder = !this.base.isTouchDevice && (drawTradeOrdersComp.elementMaximumWidth > 0 || drawExitOrderComp.elementMaximumWidth > 0);
        const hasOrdersWithinYAxisExtremes = this.hasOrdersWithinYAxisExtremes();
        if (shouldSetRightEdgePaddingForEntryOrLimitOrder && hasOrdersWithinYAxisExtremes.isInRange) {
            this.setExtreamToExitOrderIfExist();

            if (hasOrdersWithinYAxisExtremes.price && (hasOrdersWithinYAxisExtremes.price < yAxis.min || hasOrdersWithinYAxisExtremes.price > yAxis.max)) {
                const padding = (yAxis.max - yAxis.min) * 0.1; // 10% padding
                const newMin = Math.min(yAxis.min, hasOrdersWithinYAxisExtremes.price - padding);
                const newMax = Math.max(yAxis.max, hasOrdersWithinYAxisExtremes.price + padding);
                this.moveYAxisMinMax(newMin, newMax);
            }

            return;
        }

        if (this.base.marketingWebChartMode) {
            if (this.base.isExtraSmall()) {
                this.setExtreamToMinMax();
                return;
            }

            this.setExtreamToExitOrderIfExist();
        } else {
            const existingPointWithStartTimeT = this.tiChartPoints.find((x: TIChartPointModel) => x.startTimeT == Math.floor(this.chartStyleSettings.startTimeT));
            if (existingPointWithStartTimeT) {
                const diff = this.extreamMax - this.extreamMin;
                const val = existingPointWithStartTimeT.xValue + (this.chartStyleSettings.startTimeT - Math.floor(this.chartStyleSettings.startTimeT));

                this.chartStyleSettings.startTimeT = 0;
                this.extreamMin = val;
                this.extreamMax = this.extreamMin + diff;
                this.setExtreamToMinMax();

                this.chartService.requestSetFirstVisibleStartTimeT(0);
            } else if (this.chartStyleSettings.supportsFutureScrolling && this.futureScrollingEnabled && this.extraFutureBarCount > 0) {
                this.extreamMax += this.extraFutureBarCount ?? 0;
                this.scrollToExtraPaddingArea = true;
                this.setExtreamToMinMax();
            } else {
                this.setExtreamToMinMax();
            }
        }
    }

    public resetExtreamSetFlags() {
        this.extreamSet = false;
        this.positionsSet = false;
        this.ordersSet = false;
        this.chartLoaded = false;
    }

    public hasOrdersWithinYAxisExtremes(): { isInRange: boolean, minPrice?: number, maxPrice?: number, price?: number } {
        const drawTradeOrdersComp = this.getDrawTradeOrdersComponent();
        const exitOrder = this.getDrawExitOrderComponent();

        const yAxis = this.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return { isInRange: false };
        }

        const extremes = yAxis.getExtremes();
        const yMin = extremes.min;
        const yMax = extremes.max;

        // Check exit order
        if (exitOrder?.exitOrderPlotLine && !exitOrder.exitOrderPlotLine.hide) {
            const exitPrice = exitOrder.exitOrderPlotLine.position.averageCost;
            if (exitPrice >= yMin && exitPrice <= yMax) {
                return { isInRange: true, minPrice: yMin, maxPrice: yMax, price: exitPrice };
            }
        }

        // Check trade orders
        if (drawTradeOrdersComp?.tradePlotLines?.length) {
            for (const tradePlotLine of drawTradeOrdersComp.tradePlotLines) {
                const tradePrice = tradePlotLine.model.price;
                if (tradePrice >= yMin && tradePrice <= yMax) {
                    return { isInRange: true, minPrice: yMin, maxPrice: yMax, price: tradePrice };
                }
            }
        }

        return { isInRange: false };
    }

    private updatePrimitiveChartDataPoints() {
        const series = this.highStockChart?.series?.[0];
        if (!series) return;

        let needHighChartZone = false;
        if (this.highChartZonePoints.length) {
            needHighChartZone = true;
        }

        const dataPoints = TIDataPointToHighChartDataPointMapper.map(this);
        series.setData(dataPoints.points, true, false, false);

        this.highChartZonePoints = dataPoints.zones;
        if (needHighChartZone) {
            series.update({
                zones: this.highChartZonePoints,
                zoneAxis: 'x'
            } as any);
        }

        this.shouldUpdatePrimitiveChart = false;
        this.zoomPrimitiveChartToDate();
    }

    private zoomPrimitiveChartToDate() {
        if (!this.chartStyleSettings?.primitiveChartSettings?.primitiveMode) {
            return;
        }

        const totalDataPoints = this.tiChartPoints.length;
        const zoomChartTime = this.chartStyleSettings.primitiveChartSettings.zoomChartTime;
        const zoomChartToTimeDataPoint = this.tiChartPoints.find(x => zoomChartTime.valueOf() >= x.start.valueOf() && zoomChartTime.valueOf() <= x.end.valueOf());
        if (zoomChartToTimeDataPoint == null) {
            return;
        }

        const zoomChartToTimeDataPointIndex = this.tiChartPoints.findIndex(x => x.xValue == zoomChartToTimeDataPoint.xValue);
        const paddingDataPointsBeforeZoomChartToTime = Math.round(this.chartStyleSettings.primitiveChartSettings.totalBarCount * this.chartStyleSettings.primitiveChartSettings.paddingBarPercent);
        if (paddingDataPointsBeforeZoomChartToTime < 0) {
            return;
        }

        let startPosition = (zoomChartToTimeDataPointIndex - paddingDataPointsBeforeZoomChartToTime) + 1;
        if (startPosition < 0)
            startPosition = 0;

        let endPosition = (startPosition + this.chartStyleSettings.primitiveChartSettings.totalBarCount);
        if (endPosition > totalDataPoints) {
            startPosition -= (endPosition - totalDataPoints) / 2;

            if (startPosition < 0) {
                startPosition = 0;
            }

            endPosition = totalDataPoints;
        }

        this.extreamMin = startPosition;
        this.extreamMax = endPosition;
        this.setExtreamToMinMax();
    }

    private setZoomLevel() {
        const zoom = this.getDefaultZoomLevel();
        if (zoom != null && this.chartStyleSettings) {
            this.chartStyleSettings.zoomLevel = zoom;
        }

        this.currentZoom = this.chartStyleSettings?.zoomLevel ?? 0;
    }

    public getDefaultZoomLevel(): number | null {
        if (this.chartStyleSettings && this.base.pnpMode) {
            if (this.base.marketingWebChartMode) {
                if (this.currentTimeframe == Timeframe.OneMonthRange) {
                    this.chartStyleSettings.zoomLevel = 0;
                } else if (this.currentTimeframe == Timeframe.SixMonthRange) {
                    this.chartStyleSettings.zoomLevel = -8;
                } else if (this.currentTimeframe == Timeframe.OneYearRange || this.currentTimeframe == Timeframe.YearToDateRange) {
                    this.chartStyleSettings.zoomLevel = -9;
                } else if (this.currentTimeframe == Timeframe.FiveYearRange) {
                    this.chartStyleSettings.zoomLevel = -6;
                }
            } else {
                if (this.currentTimeframe == Timeframe.OneMonthRange) {
                    this.chartStyleSettings.zoomLevel = 0;
                } else if (this.currentTimeframe == Timeframe.SixMonthRange) {
                    this.chartStyleSettings.zoomLevel = -8;
                } else if (this.currentTimeframe == Timeframe.OneYearRange || this.currentTimeframe == Timeframe.YearToDateRange) {
                    this.chartStyleSettings.zoomLevel = -9;
                } else if (this.currentTimeframe == Timeframe.FiveYearRange) {
                    this.chartStyleSettings.zoomLevel = -6;
                }
            }
        }

        return null;
    }

    public setChartComponentStyles() {
        this.base.setLastPricePositions();
    }

    public redrawHighChartIndicator() {
        const allIndicatorOptions = this.getAllActiveIndicatorOptions().filter((x: IndicatorOptionModel) => x.highChartIndicator);
        for (const indicatorOption of allIndicatorOptions) {
            this.removeHighChartIndicator(indicatorOption.value);
            this.addHighChartIndicator(indicatorOption.value);
        }
    }

    private subscribe10sRefreshChartData() {
        this.chartService.subscribeUpdate10sRefreshChartData((data: Array<TIChartPointModel>) => {
            if (data?.length <= 0 || this.requestingHistoryData) {
                return;
            }

            for (const point of data) {
                TraceLoggingHelpers.log('Start update point , updateChartData', point)
                this.mainChart.updateChartData(point);
            }

            this.base.setLastPricePositions();
        });
    }

    private subscribe10sRefreshIndicatorsData() {
        this.chartService.subscribeUpdate10sRefreshIndicatorsData((data: Array<TIIndicatorDataModel>) => {
            if (!data?.length || this.requestingHistoryData) {
                return;
            }

            this.indicators.updateIndicatorsData(data);
            this.base.setLastPricePositions();
        });
    }

    private subscribeUpdateChartHistoryData() {
        this.chartService.subscribeUpdateChartHistoryData((data: Array<TIChartPointModel>) => {
            if (!data?.length) {
                this.historyDataEnded = true;
                return;
            }

            if (this.base.marketingWebChartMode) {
                // TraceLoggingHelpers.log('History data marketingWebChartMode ', this.firstDataPoint, data);
                if (data?.length == 1) {
                    this.historyDataEnded = true;
                    return;
                }
            }

            TraceLoggingHelpers.log('History data');
            this.mainChart.updateHistoryChartData(data);
        });
    }

    private subscribeUpdateChartComparisonHistoryData() {
        this.chartService.subscribeUpdateChartHistoryComparisonData((data: TIComparisonHistoryDataModel) => {
            this.highChartComparisonBuilder.updateComparisonHistoryData(data);
        });
    }

    private subscribeUpdateHistoryIndicatorsData() {
        this.chartService.subscribeUpdateHistoryIndicatorsData((data: Array<TIIndicatorDataModel>) => {
            setTimeout(() => {
                setTimeout(() => {
                    TraceLoggingHelpers.log('this.requestingHistoryData = false');
                    // this.highChartAxisBuilder.buildAxisComponents();
                    this.requestingHistoryData = false;
                }, 100);

                this.indicators.updateHistoryChartIndicatorData(data);
            }, 50);
        });
    }

    private subscribeWindowMinimizeUpdate() {
        this.chartService.subscribeWindowMinimizeUpdate((shouldRedraw: boolean) => {
            if (shouldRedraw) {
                this.highStockChart?.reflow();
                this.highStockChart?.redraw();
            }
        });
    }

    private subscribeChartDataLastPriceChange() {
        this.chartService.subscribeLastPriceData((price: number) => {
            this.lastPrice = price;
            this.onLastPriceChange();
        });
    }

    private orderSetTimer: any = null;
    private subscribeChartDataPositionViewModel() {
        this.chartService.subscribeCurrentPositionViewModel((data: Array<CurrentPositionViewModel>) => {
            this.currentPositionViewModels = data;
            this.onPositionViewModelUpdate();

            this.positionsSet = true;
            this.initializeChartExtream();

            clearTimeout(this.orderSetTimer);
            this.orderSetTimer = setTimeout(() => {
                if (this.currentPositionViewModels.length == 0 && !this.ordersSet) {
                    this.ordersSet = true;
                    const orderSetComp = this.getDrawTradeOrdersComponent();
                    if (orderSetComp) {
                        orderSetComp.initialize = true;
                    }

                    const exitOrderComp = this.getDrawExitOrderComponent();
                    if (exitOrderComp) {
                        exitOrderComp.initialize = true;
                    }
                    this.initializeChartExtream();
                }
            }, 1000);
        });
    }

    private subscribeTradeOrdersData() {
        this.chartService.subscribeDrawTradeOrdersData((tradeOrders: Array<TradeOrderDataModel>) => {
            this.ordersSet = true;
            clearTimeout(this.orderSetTimer);
            this.initializeChartExtream();
        });
    }

    private initializeChartExtream() {
        if (this.positionsSet && this.ordersSet && this.chartLoaded && !this.extreamSet) {
            this.extreamSet = true;
            clearTimeout(this.setExtreamAfterChartStartTimer);
            this.setExtreamAfterChartStartTimer = setTimeout(() => {
                this.setExtreamAfterChartStart();
            }, 350);
        }
    }

    private subscribeAuthorizationsData() {
        this.chartService.subscribe_Authorizations((data: Array<AuthorizationModel>) => {
            this.authorizations = data;
        });
    }

    private subscribeRealtimeData() {
        this.chartService.subscribeRealtimeData((data: RealtimeTradeDataModel) => this.whenGetNewTradeData(data))
    }

    private whenGetNewTradeData(tradeData: RealtimeTradeDataModel) {
        if (!this.ready || this.requestingHistoryData || tradeData.symbol != this.symbol) {
            return;
        }

        this.mainChart.updateRealTimeChartData(tradeData);
    }

    private requestSaveFutureBarCount(barCount: number) {
        if (!this.chartStyleSettings?.rightEdgePaddingSaveEnabled) {
            return;
        }

        if (barCount > this.chartStyleSettings.rightEdgePaddingBarsDefault) {
            barCount = this.chartStyleSettings.rightEdgePaddingBarsDefault;
        }

        this.extraFutureBarCount = barCount;
        this.chartService.requestSaveExtraFutureBarCount(barCount);
    }

    private requestSaveStartTimeTWithVisibleBar() {
        const point = this.tiChartPoints.find((x: TIChartPointModel) => x.xValue == Math.floor(this.extreamMin));
        if (this.shouldHideHistoryModeButton()) {
            this.chartService.requestSetFirstVisibleStartTimeT(0);
        } else if (point) {
            const val = point.startTimeT + (this.extreamMin - Math.floor(this.extreamMin));
            this.chartService.requestSetFirstVisibleStartTimeT(val);
        }
    }

    private requestSaveYAxisHeightState() {
        const yAxises = this.highStockChart?.yAxis;
        if (!yAxises?.length || this.base.pnpMode) {
            return;
        }

        const totalHeight = this.highStockChart.chartHeight;
        const totalWidth = this.highStockChart.chartWidth;
        const spacingTop = this.highStockChart.spacingBox.y;
        const usableHeight = totalHeight - spacingTop;
        // if (this.chartStyleSettings?.chartYAxisStateModel?.chartHeight == totalHeight && this.chartStyleSettings?.chartYAxisStateModel?.chartWidth == totalWidth) {
        //     return;
        // }

        const model = new ChartYAxisStateModel();
        model.states = [];

        model.chartHeight = totalHeight;
        model.chartWidth = totalWidth;

        // let shouldSave = false;
        for (const yAxis of yAxises) {
            if (!yAxis?.userOptions) {
                continue;
            }

            const id = yAxis.userOptions.id;
            const isMainChart = id.indexOf(HighChartBuilder.mainChartAreaName) > -1;
            const axisTop = isMainChart ? 0 : yAxis.top - spacingTop;
            const axisHeight = yAxis.height;
            const topPercentage = (axisTop / usableHeight) * 100;
            const heightPercentage = (axisHeight / usableHeight) * 100;

            if (this.highChartBuilder.initIndicatorHeight[id]) {
                this.highChartBuilder.initIndicatorHeight[id].height = yAxis.height + 'px';
                this.highChartBuilder.initIndicatorHeight[id].top = yAxis.top + 'px';
            }

            model.states.push({
                name: yAxis.userOptions.id,
                height: heightPercentage + '%',
                top: topPercentage + '%'
            });
        }

        this.chartService.requestSaveYAxisHeightState(model);
    }

    public onExtremeChange(ev: any) {
        setTimeout(() => {
            this.requestSaveStartTimeTWithVisibleBar();
            this.requestSaveYAxisHeightState();
        }, 150);

        for (const functions of this.watchExtremesChangeCallbacks.values()) {
            for (const fx of functions) {
                fx(ev);
            }
        }
    }

    public subscribeExtremeChange(key: string, fx: Function) {
        let functions: Array<Function> = [];
        if (this.watchExtremesChangeCallbacks.has(key)) {
            functions = this.watchExtremesChangeCallbacks.get(key);
        }

        functions.push(fx);
        this.watchExtremesChangeCallbacks.set(key, functions);
    }

    public onRedraw() {
        for (const functions of this.watchRedrawChangeCallbacks.values()) {
            for (const fx of functions) {
                fx();
            }
        }
    }

    public subscribeRedrawChange(key: string, fx: Function) {
        let functions: Array<Function> = [];
        if (this.watchRedrawChangeCallbacks.has(key)) {
            functions = this.watchRedrawChangeCallbacks.get(key);
        }

        functions.push(fx);
        this.watchRedrawChangeCallbacks.set(key, functions);
    }

    public onLoaded() {
        this.chartLoaded = true;
        for (const functions of this.watchChartLoadedCallbacks.values()) {
            for (const fx of functions) {
                fx();
            }
        }

        this.initializeChartExtream();
    }

    public subscribeChartLoad(key: string, fx: Function) {
        let functions: Array<Function> = [];
        if (this.watchChartLoadedCallbacks.has(key)) {
            functions = this.watchChartLoadedCallbacks.get(key);
        }

        functions.push(fx);
        this.watchChartLoadedCallbacks.set(key, functions);
    }

    private onScrolled() {
        for (const functions of this.watchChartScrolledCallbacks.values()) {
            for (const fx of functions) {
                fx();
            }
        }
    }

    public subscribeChartScroll(key: string, fx: Function) {
        let functions: Array<Function> = [];
        if (this.watchChartScrolledCallbacks.has(key)) {
            functions = this.watchChartScrolledCallbacks.get(key);
        }

        functions.push(fx);
        this.watchChartScrolledCallbacks.set(key, functions);
    }

    public onPopupChange(open: boolean, event: any) {
        for (const functions of this.watchHighChartPopupCallbacks.values()) {
            for (const fx of functions) {
                fx(open, event);
            }
        }
    }

    public subscribePopupChange(key: string, fx: Function) {
        let functions: Array<Function> = [];
        if (this.watchHighChartPopupCallbacks.has(key)) {
            functions = this.watchHighChartPopupCallbacks.get(key);
        }

        functions.push(fx);
        this.watchHighChartPopupCallbacks.set(key, functions);
    }

    public onAnnotationAdd(event: any, fromCache: boolean) {
        for (const functions of this.watchAnnotationAddedCallbacks.values()) {
            for (const fx of functions) {
                fx(event, fromCache);
            }
        }
    }

    public subscribeAnotationAdd(key: string, fx: Function) {
        let functions: Array<Function> = [];
        if (this.watchAnnotationAddedCallbacks.has(key)) {
            functions = this.watchAnnotationAddedCallbacks.get(key);
        }

        functions.push(fx);
        this.watchAnnotationAddedCallbacks.set(key, functions);
    }

    public onAnnotationAfterUpdate(event: any) {
        for (const functions of this.watchAnnotationAfterUpdatedCallbacks.values()) {
            for (const fx of functions) {
                fx(event);
            }
        }
    }

    public subscribeAnotationAfterUpdate(key: string, fx: Function) {
        let functions: Array<Function> = [];
        if (this.watchAnnotationAfterUpdatedCallbacks.has(key)) {
            functions = this.watchAnnotationAfterUpdatedCallbacks.get(key);
        }

        functions.push(fx);
        this.watchAnnotationAfterUpdatedCallbacks.set(key, functions);
    }

    public onLastPriceChange() {
        for (const functions of this.watchLastPriceChangeCallbacks.values()) {
            for (const fx of functions) {
                fx(this.lastPrice);
            }
        }
    }

    public subscribeLastPriceChange(key: string, fx: Function) {
        let functions: Array<Function> = [];
        if (this.watchLastPriceChangeCallbacks.has(key)) {
            functions = this.watchLastPriceChangeCallbacks.get(key);
        }

        functions.push(fx);
        this.watchLastPriceChangeCallbacks.set(key, functions);
    }

    public onPositionViewModelUpdate() {
        for (const functions of this.watchPositionViewModelsUpdateCallbacks.values()) {
            for (const fx of functions) {
                fx(this.currentPositionViewModels);
            }
        }
    }

    public subscribePositionViewModelUpdate(key: string, fx: Function) {
        let functions: Array<Function> = [];
        if (this.watchPositionViewModelsUpdateCallbacks.has(key)) {
            functions = this.watchPositionViewModelsUpdateCallbacks.get(key);
        }

        functions.push(fx);
        this.watchPositionViewModelsUpdateCallbacks.set(key, functions);
    }

    public removeSubscriberByKey(key: string) {
        if (this.watchExtremesChangeCallbacks.has(key)) {
            this.watchExtremesChangeCallbacks.delete(key);
        }

        if (this.watchRedrawChangeCallbacks.has(key)) {
            this.watchRedrawChangeCallbacks.delete(key);
        }

        if (this.watchChartLoadedCallbacks.has(key)) {
            this.watchChartLoadedCallbacks.delete(key);
        }

        if (this.watchHighChartPopupCallbacks.has(key)) {
            this.watchHighChartPopupCallbacks.delete(key);
        }

        if (this.watchAnnotationAddedCallbacks.has(key)) {
            this.watchAnnotationAddedCallbacks.delete(key);
        }

        if (this.watchAnnotationAfterUpdatedCallbacks.has(key)) {
            this.watchAnnotationAfterUpdatedCallbacks.delete(key);
        }

        if (this.watchLastPriceChangeCallbacks.has(key)) {
            this.watchLastPriceChangeCallbacks.delete(key);
        }

        if (this.watchPositionViewModelsUpdateCallbacks.has(key)) {
            this.watchPositionViewModelsUpdateCallbacks.delete(key);
        }
    }

    private getDefaultTotalXValuePerScreen(): number {
        if (this.base.marketingWebChartMode) {
            if (this.element.clientWidth <= 350) {
                return 20;
            }

            return 40;
        }

        if (this.element.clientWidth <= 450)
            return 20;
        else if (this.element.clientWidth <= 640)
            return 40;
        else if (this.element.clientWidth <= 830)
            return 60;

        return 100;
    }

    private onBodyClick() {
        this.base.domEvents.onScreenClick(this.id, () => {
            this.chartService.notifyChartHideContextMenu();
        });
    }

    private resizeTimmer = null as any;
    private onResize() {
        this.base.domEvents.onResize(this.id, () => {
            clearTimeout(this.resizeTimmer);
            this.resizeTimmer = setTimeout(() => {
                this.handleResize();
            }, 250);
        });
    }

    private handleResize() {
        if (this.base.isPreview) {
            return;
        }

        if (this.base.marketingWebChartMode) {
            this.afterResize();
        } else {
            if (this.base.isBodySizeValid()) {
                if (this.waitForRetryStart) {
                    this.start();
                } else {
                    this.afterResize();
                }
            }
        }
    }

    public getCurrentXValueFromInitialDataPoints(startTime: number): number {
        let toReturn = 0;
        if (this.tiChartPoints?.length) {
            let lastDataPoint: TIChartPointModel | null = null;
            const dataPointList: Array<TIChartPointModel> = [];
            for (const dataPoint of this.tiChartPoints) {
                const thisTimeT = dataPoint.startTimeT;
                if (startTime <= thisTimeT) {
                    if (null == lastDataPoint) {
                        toReturn = dataPoint.xValue;
                    } else {
                        const min = lastDataPoint.startTimeT;
                        const max = dataPoint.startTimeT;
                        const percentBetween = (startTime - min) / (max - min);

                        const minXValue = lastDataPoint.xValue;
                        const maxXValue = dataPoint.xValue;
                        const differenceInX = (maxXValue - minXValue) * percentBetween;
                        toReturn = minXValue + differenceInX;
                        break;
                    }
                }

                lastDataPoint = dataPoint;
                dataPointList.push(dataPoint);
            }
            // The following logic captures the case where the StartTimeT is greater than any current data.
            // Without this data the trendline will not be read correctly from the cache.
            if (toReturn == 0 && dataPointList.length > 0) {
                lastDataPoint = null;
                // Reverse the list
                dataPointList.reverse();
                for (const dataPoint of dataPointList) {
                    const thisTimeT = dataPoint.startTimeT;
                    if (startTime >= thisTimeT) {
                        if (null == lastDataPoint) {
                            toReturn = dataPoint.xValue;
                            break;
                        } else {
                            // const max = lastDataPoint.startTimeT;
                            // const min = dataPoint.startTimeT;
                            // const percentBetween = (max - startTime) / (max - min);

                            // const maxXValue = lastDataPoint.xValue;
                            // const minXValue = dataPoint.xValue;
                            // const differenceInX = (minXValue - maxXValue) * percentBetween;
                            // toReturn = maxXValue + differenceInX;
                            // break;
                        }
                    }
                    lastDataPoint = dataPoint;
                }
            }
        }

        return toReturn;
    }

    public clearEMAClouldData() {
        this.indicators.canUpdateEMACloud = false;
        this.indicators.clearData(this.tiIndicatorsData);
    }

    public removeEMAClouldSeries() {
        this.indicators.canUpdateEMACloud = false;
        this.indicators.removeEMACloudSeries(this.tiIndicatorsData);
    }

    public hasEMAClouldSeries(): boolean {
        return this.indicators.hasEMACloudSeries(this.tiIndicatorsData) && this.getAllActiveIndicatorOptions().some(x => x.value == IndicatorType.EMACloud);
    }

    public destroyHighCharts() {
        try {
            if (this.highStockChart?.destroy) {
                this.highStockChart.destroy();
                this.highStockChart = null;
            }
        } catch { }
    }

    public setEMACloundData() {
        if (this.indicators.canUpdateEMACloud || !this.hasEMAClouldSeries()) return;
        this.indicators.setData(this.tiIndicatorsData);
        this.indicators.canUpdateEMACloud = true;

        // ema cloud settings for website
        if (this.base.marketingWebChartMode) {
            const popup = this.base.getComponentsByType(IndicatorManagerPopupComponent)?.[0] as IndicatorManagerPopupComponent;
            popup?.syncEMACloud(false);
        } else {
            const popup = this.base.getComponentsByType(EMACloudSettingsPopupComponent)?.[0] as EMACloudSettingsPopupComponent;
            popup?.render(false);
        }

        setTimeout(() => {
            const drawEMACloudBuySellArrowComponent = this.base.getComponentsByType(DrawEMACloudBuySellArrowComponent)?.[0] as DrawEMACloudBuySellArrowComponent;
            drawEMACloudBuySellArrowComponent?.enable();
        }, 300);
    }

    private afterResizeTimer: any;
    public afterResize() {
        if (this.base.marketingWebChartMode) {
            const timeframe = this.base.getComponentsByType(TimeframeIconComponent)[0] as TimeframeIconComponent;
            if (timeframe) {
                timeframe.setWebChartSize();
            }
        }
        this.setChartComponentStyles();
        clearTimeout(this.afterResizeTimer);
        this.afterResizeTimer = setTimeout(() => {
            this.adjustBarAfterResize();
            this.highChartAxisBuilder.buildAxisComponents();
        }, 100);
    }

    public getXValueByDateTime(dateTime: Date): number {
        let mostRecentEndTime = new Date(-8640000000000000); // Equivalent to DateTime.MinValue
        let oldestDataPoint: TIChartPointModel | null = null;

        for (let i = this.tiChartPoints.length - 1; i > 0; i--) {
            const dataPoint = this.tiChartPoints[i];
            let previousDataPoint: TIChartPointModel | null = null;

            if (dataPoint !== null) {
                if (mostRecentEndTime < dataPoint.end) {
                    mostRecentEndTime = dataPoint.end;
                }

                if (i - 1 >= 0) {
                    previousDataPoint = this.tiChartPoints[i - 1];
                }

                if (previousDataPoint === null || dataPoint.start <= dateTime) {
                    return dataPoint.xValue;
                }

                oldestDataPoint = dataPoint;
            }
        }

        if (oldestDataPoint !== null) {
            return oldestDataPoint.xValue;
        }

        return 0;
    }

    public setExtreamFromHistoryButton(toExitOrder: boolean) {
        this.scrollToExtraPaddingArea = false;
        this.scrollToExtraPaddingAreaTimerInit = false;
        this.highChartAxisBuilder.redrawYAxis = true;
        this.highChartAxisBuilder.manualMoveYAxisScale = false;
        this.highChartAxisBuilder.manualExpandYAxisScale = false;
        this.highStockChart?.yAxis?.[0].setExtremes(null, null)

        this.yAxisMoved = false;
        this.extreamMin = this.originalMinimumXValueWithZoom;
        this.extreamMax = this.originalMaximumXValue;
        this.setExtremes(this.extreamMin, this.extreamMax);
        setTimeout(() => {
            this.highChartAxisBuilder.buildAxisComponents();

            if (toExitOrder) {
                setTimeout(() => {
                    const pnl = this.base.getComponentsByType(PnlComponent)?.[0] as PnlComponent;
                    const drawTradeOrdersComp = this.getDrawTradeOrdersComponent();
                    if (((pnl && !!pnl.selectedAccount) || drawTradeOrdersComp?.hasAnyTradeOrders()) && !this.base.isTouchDevice) {
                        this.setExtreamToExitOrderIfExist();
                    }
                }, 25);
            }
        }, 50);
    }

    private adjustBarAfterResize() {
        const axis = this.highStockChart?.xAxis[0];
        if (!axis) {
            return;
        }

        if (this.currentScreen == this.element.clientWidth && !this.base.marketingWebChartMode) {
            return;
        }

        this.currentScreen = this.element.clientWidth;

        if (this.base.marketingWebChartMode) {
            this.highStockChart.reflow();
        } else {
            this.calcMinMaxTimeframeFromXValue(this.originalMaximumXValue, true);
            this.setExtremes(this.originalMinimumXValueWithZoom, this.originalMaximumXValue);
            this.highStockChart.reflow();
        }
    }
}
